/**
 * Emits hreflang + canonical + (conditional) robots noindex tags.
 *
 * Mount once near the top of <App />. For each route, it:
 *  1. Computes the locale-less ("stripped") pathname.
 *  2. Emits hreflang alternates ONLY for locales that actually have real
 *     translated content (controlled by `REAL_CONTENT_LOCALES` and an
 *     optional per-page `pageLocalesAvailable` override).
 *  3. Emits regional English hreflang variants (en-AU, en-GB, en-US, en-CA)
 *     pointing at the same English URL — cleaner than duplicating URLs.
 *  4. Emits <meta name="robots" content="noindex,follow"> on any URL that
 *     has a non-English locale prefix (e.g. /vi/..., /fr/...) UNTIL that
 *     page has real translated content for that locale. This prevents
 *     Google from indexing thin duplicate language pages without removing
 *     them from the user-facing UI.
 *
 * Pages that have richer locale availability can pass their own
 * `localesAvailable` prop (e.g. `['en','vi']`) to declare which locales
 * have real translated content for that specific URL.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { parsePathLocale, buildLocalizedPath } from './LocaleRouter';
import {
  REAL_CONTENT_LOCALES,
  REGIONAL_EN_HREFLANGS,
  isRealContentLocale,
  hasLocalePrefix,
} from '../config/i18nConfig';

const SITE = 'https://vietclinics.com';

/**
 * @param {Object} props
 * @param {string[]} [props.localesAvailable] - Optional per-page list of locales
 *   with real translated content. Intersects with REAL_CONTENT_LOCALES.
 *   When omitted, defaults to REAL_CONTENT_LOCALES.
 */
const HreflangTags = ({ localesAvailable = null }) => {
  const { pathname, search } = useLocation();
  const { stripped } = parsePathLocale(pathname);

  // 1. Which locales should we emit hreflang for?
  const liveLocales = REAL_CONTENT_LOCALES.filter((l) =>
    isRealContentLocale(l, localesAvailable),
  );

  // 2. Should this URL be noindexed? (locale-prefixed routes that don't yet
  //    have real translated content)
  const onLocalePrefix = hasLocalePrefix(pathname);
  const { locale: currentLocale } = parsePathLocale(pathname);
  const currentLocaleHasContent = isRealContentLocale(currentLocale, localesAvailable);
  const localeShouldNoIndex = onLocalePrefix && !currentLocaleHasContent;

  // 2b. Non-production hosts (dev preview, *.preview.emergentagent.com) must be
  //     noindex so the preview never competes with production in search.
  //     Production (vietclinics.com) stays indexable.
  const PROD_HOSTS = ['vietclinics.com', 'www.vietclinics.com'];
  const isPreviewHost =
    typeof window !== 'undefined' && !PROD_HOSTS.includes(window.location.hostname);

  const shouldNoIndex = localeShouldNoIndex || isPreviewHost;

  const englishHref = `${SITE}${buildLocalizedPath(stripped, 'en')}${search}`;

  return (
    <Helmet>
      {/* Robots: noindex,follow for thin locale-prefixed pages */}
      {shouldNoIndex && <meta name="robots" content="noindex,follow" />}

      {/* hreflang alternates for real-content locales only */}
      {liveLocales.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${SITE}${buildLocalizedPath(stripped, l)}${search}`}
        />
      ))}

      {/* Regional English variants — all point to the same English URL */}
      {liveLocales.includes('en') &&
        REGIONAL_EN_HREFLANGS.filter((tag) => tag !== 'en').map((tag) => (
          <link key={tag} rel="alternate" hrefLang={tag} href={englishHref} />
        ))}

      {/* x-default → English */}
      {liveLocales.includes('en') && (
        <link rel="alternate" hrefLang="x-default" href={englishHref} />
      )}
    </Helmet>
  );
};

export default HreflangTags;
